import { FileText, MessageSquare, Scale, Users, type LucideIcon } from "lucide-react";
import { TERMS_VERSION } from "@/components/legal/termsVersion";

export interface PricingFeatureConfig {
  text: string;
  enabled?: boolean;
  badge?: string;
}

/**
 * The five automatic-renewal offer terms required by Cal. Bus. & Prof. Code §17601(2)(A)-(E).
 * Rendered in <AutoRenewalDisclosure /> beside the consent checkbox, and snapshotted into the
 * consent record with `consentLabel` (§17602(a)(1), (a)(6)). Amounts must match the Stripe Price.
 */
export interface AutoRenewalTermsConfig {
  /** (A) that the subscription continues until the consumer cancels */
  continuesUntilCancelled: string;
  /** (B) the cancellation policy — how to cancel and when it takes effect */
  cancellationPolicy: string;
  /** (C) the recurring charge, and that the amount may change */
  recurringCharge: string;
  /** (D) the length of the renewal term */
  renewalTerm: string;
  /** (E) any minimum purchase obligation */
  minimumPurchase: string;
  /** One-line restatement of amount + cadence shown beside the consent checkbox. */
  consentLabel: string;
  /**
   * Post-promotional price and the duration of the promotional period, where an introductory or
   * promotional price applies. Leave undefined until the real values are read off the Stripe
   * Dashboard — shipping a guess here is worse than shipping nothing.
   */
  promoDisclosure?: string;
}

/** Exact §17601(2) lines shown in the POS disclosure (render order = snapshot order). */
export function autoRenewalDisclosureLines(t: AutoRenewalTermsConfig): string[] {
  return [
    t.continuesUntilCancelled,
    t.cancellationPolicy,
    t.recurringCharge,
    t.renewalTerm,
    t.minimumPurchase,
  ].filter((line) => line.trim().length > 0);
}

/** Single-paragraph form used in the expandable POS disclosure. */
export function autoRenewalDisclosureText(t: AutoRenewalTermsConfig): string {
  return autoRenewalDisclosureLines(t).join(" ");
}

export interface PricingPlanConfig {
  id: string;
  title: string;
  price: string;
  period: string;
  billedText?: string;
  badge?: string;
  featured?: boolean;
  cta: string;
  autoRenewal: AutoRenewalTermsConfig;
}

export interface AdminFeatureConfig {
  enabled: boolean;
  crmEnabled: boolean;
  evaluationsEnabled: boolean;
  userFeedbackEnabled: boolean;
  pointsEnabled: boolean;
  systemHealthEnabled: boolean;
  emailAnalyticsEnabled: boolean;
  marketingIntelligenceEnabled: boolean;
  adminPath: string;
}

export interface PrivacyFeatureConfig {
  cookieConsentEnabled: boolean;
}

export interface ProductConfig {
  pricing: {
    backgroundClass: string;
    heroTitle: string;
    heroDescription: string;
    defaultPlan: string;
    plans: PricingPlanConfig[];
    features: PricingFeatureConfig[];
    promoText: string;
    /** Free-account offer shown above the paid plans to signed-out visitors. */
    freeOffer: { title: string; description: string; cta: string };
    /** Published cancellation contact — Cal. Bus. & Prof. Code §17602(c). */
    cancellationEmail: string;
    /**
     * Version stamp for the Terms of Service + auto-renewal disclosure currently shown.
     * Recorded against every consent (§17602(a)(6)) so a stored consent can be tied back to the
     * exact terms it was given under. Sourced from TERMS_VERSION — bump it there, not here.
     */
    termsVersion: string;
    trustTitle: string;
    trustItems: Array<{ icon: LucideIcon; title: string; description: string }>;
    faqItems: Array<{ question: string; answer: string; link?: { href: string; label: string } }>;
  };
  admin: AdminFeatureConfig;
  privacy: PrivacyFeatureConfig;
  canonicalUi: {
    home: {
      eyebrow: string;
      title: string;
      subtitle: string;
      note: string;
      sampleQuestion: string;
      sampleAnswer: string;
      /** Structured preview mirroring the real AnswerBody sections. */
      sampleAnswerSections: {
        statutes: string[];
        homeowners: string[];
        mistake: string;
        nextStep: string;
        references: string[];
      };
      sampleProductName: string;
      sampleLawHeading: string;
      trustPoints: string[];
      /** One-tap questions in the hero ask box. */
      starterQuestions: string[];
      quickStartTitle: string;
      quickStartSubtitle: string;
      quickStartSteps: Array<{ title: string; description: string }>;
      audienceTitle: string;
      audiences: Array<{ title: string; description: string }>;
      ctaTitle: string;
      ctaDescription: string;
      ctaButton: string;
    };
    dashboard: {
      title: string;
      assistantTitle: string;
      assistantDescription: string;
      assistantBody: string;
      quickChatDescription: string;
    };
    chat: {
      emptyPrompt: string;
      suggestedQuestions: string[];
      compareWithLawTitle: string;
    };
  };
}

export const CHATGPT_COMPARISON_QUESTION = "How is FloridaHOALaw.ai different from ChatGPT?";
export const CHATGPT_COMPARISON_SLUG = "how-is-floridahoalaw-ai-different-from-chatgpt";

export const productConfig: ProductConfig = {
  pricing: {
    backgroundClass: "bg-gradient-to-br from-blue-50 via-white to-orange-50",
    heroTitle: "Florida HOA Legal AI",
    heroDescription: "Research Florida HOA and condominium law from $49 per year",
    defaultPlan: "annual",
    plans: [
      {
        id: "monthly",
        title: "Monthly",
        price: "$5",
        period: "/month",
        cta: "Get Monthly Access",
        autoRenewal: {
          continuesUntilCancelled: "",
          cancellationPolicy:
            "Access ends when the paid period ends; no partial refunds.",
          recurringCharge:
            "$5.00/month. We email you 7–30 days before any price change.",
          renewalTerm: "Renews monthly.",
          minimumPurchase: "No minimum purchase.",
          consentLabel:
            "I understand my payment method will be charged $5.00 every month, automatically, until I cancel.",
        },
      },
      {
        id: "annual",
        title: "Annual",
        price: "$49",
        period: "/year",
        billedText: "Save $11 vs. monthly",
        badge: "Save $11/year",
        featured: true,
        cta: "Start Researching HOA Law",
        autoRenewal: {
          continuesUntilCancelled: "",
          cancellationPolicy:
            "Access ends when the paid period ends; no partial refunds.",
          recurringCharge:
            "$49.00/year. We email you 7–30 days before any price change.",
          renewalTerm: "Renews every 12 months.",
          minimumPurchase: "No minimum purchase.",
          consentLabel:
            "I understand my payment method will be charged $49.00 every year, automatically, until I cancel.",
        },
      },
    ],
    features: [
      { text: "Unlimited AI questions about Florida HOA law" },
      { text: "Answers based on Florida Statutes Ch. 718 and Ch. 720" },
      { text: "Unlimited questions about your uploaded HOA documents" },
      { text: "Source-cited responses with statute references" },
      { text: "24/7 instant access - Export history - Priority support" },
    ],
    // $5/month is the standard price, not an introductory rate. The plan previously carried an
    // "Introductory Price" badge, which implied a future increase that was never scheduled —
    // a promotional-price representation with no disclosed end date or follow-on price
    // (Cal. Bus. & Prof. Code §17602(b)(1), and misleading under FDUTPA / DTPA §17.46).
    // Do not re-add the badge unless an increase is actually planned AND disclosed in
    // autoRenewal.promoDisclosure.
    promoText: "Cancel anytime. No contracts or hidden fees.",
    freeOffer: {
      title: "Free account",
      description: "5 free questions — about the law or your uploaded HOA documents — plus 1 document review. No credit card required.",
      cta: "Create free account",
    },
    cancellationEmail: "support@floridahoalaw.ai",
    // Sourced from the terms component so the recorded version can never drift from the text
    // the consumer actually accepted. Bump it there, not here.
    termsVersion: TERMS_VERSION,
    trustTitle: "Why homeowners trust FloridaHOALaw.ai",
    trustItems: [
      {
        icon: Scale,
        title: "Based on official statutes",
        description: "Responses are based on Florida Statutes Ch. 718 and Ch. 720 and source-cited materials",
      },
      {
        icon: MessageSquare,
        title: "Source-cited AI answers",
        description: "Answers include citations to the statute or document sections they draw on, so you can check the source",
      },
      {
        icon: FileText,
        title: "Upload your governing documents",
        description: "Analyze your CC&Rs, bylaws, and rules for personalized answers",
      },
      {
        icon: Users,
        title: "Built for HOA stakeholders",
        description: "Useful for homeowners, board members, and property managers",
      },
    ],
    faqItems: [
      {
        question: "Is there a free account? Do I need a credit card?",
        answer: "Yes. A free account includes 5 questions and 1 document review, and no credit card is required to sign up. Upgrade to a paid plan whenever you need more.",
      },
      {
        question: "What do I get with a paid plan?",
        answer: "Unlimited questions about Florida HOA law based on Florida Statutes Chapters 718 and 720, uploads of your governing documents for personalized answers, citations to the statute or document sections behind each answer, and priority support. Cancel anytime; your access continues through the end of your billing period.",
      },
      {
        question: "Can I cancel anytime?",
        answer: "Yes. Cancel anytime from your account settings. You'll retain access through the end of your current billing period with no further charges.",
      },
      {
        question: "Can I upload my HOA governing documents?",
        answer: "Yes. Upload your CC&Rs, bylaws, rules, and other governing documents, then ask questions answered from those documents, with citations to the sections they come from. A free account can upload documents and use its 5 free questions on them.",
      },
      {
        question: "What laws are included?",
        answer: "The platform covers Florida Statutes Chapter 718 (Condominiums) and Chapter 720 (Homeowners Associations). You can also upload your own governing documents for personalized answers.",
      },
      {
        question: CHATGPT_COMPARISON_QUESTION,
        answer: "ChatGPT is an excellent general-purpose AI assistant. FloridaHOALaw.ai is built specifically for Florida HOA law: answers grounded in Florida Statutes Chapters 718 and 720 with statutory citations, and questions answered from your own uploaded governing documents.",
        link: { href: `/questions/${CHATGPT_COMPARISON_SLUG}`, label: "Read the full comparison →" },
      },
      {
        question: "Is this legal advice?",
        answer: "No. FloridaHOALaw.ai is a research and reference tool, not a law firm. It does not create an attorney-client relationship. For advice specific to your situation, consult a licensed Florida attorney.",
      },
      {
        question: "How can I use this throughout HOA ownership or board service?",
        answer: "Questions can arise throughout HOA ownership and board service, from architectural applications and maintenance responsibilities to budgets, meetings, and elections. A subscription provides continued access within the published plan limits whenever new research needs arise.",
      },
      {
        question: "How accurate are the answers?",
        answer: "AI responses cite the specific statute or document section they draw from. Like any research tool, you should verify important decisions independently or with a qualified attorney.",
      },
    ],
  },
  admin: {
    enabled: true,
    crmEnabled: true,
    evaluationsEnabled: true,
    userFeedbackEnabled: true,
    pointsEnabled: false,
    systemHealthEnabled: true,
    emailAnalyticsEnabled: true,
    marketingIntelligenceEnabled: true,
    adminPath: "/admin",
  },
  privacy: {
    // The banner is wired into App.tsx and this flag actually controls it —
    // previously the component existed but was never rendered, so flipping this
    // did nothing. Turned on 2026-09-07 per product decision, superseding the
    // earlier deliberate off-state. Note it is an informational notice, not a
    // consent gate — it does not hold analytics back until the visitor accepts.
    cookieConsentEnabled: true,
  },
  canonicalUi: {
    home: {
      eyebrow: "AI + Florida HOA law intelligence",
      title: "Florida HOA & Condo Law",
      subtitle: "Instant answers based on Florida Statutes Ch. 718 and Ch. 720 - for homeowners, board members, and managers.",
      note: "Create a free account to continue: 5 free questions + 1 document review. No credit card required.",
      sampleQuestion: "Can the board fine me without a hearing?",
      sampleAnswer: "Usually no - Florida law requires at least 14 days' written notice and a hearing before a committee that does not include officers, directors, or employees.",
      sampleAnswerSections: {
        statutes: [
          "Fla. Stat. § 720.305 - HOA fines require 14 days' notice and a hearing before an independent committee.",
          "Fla. Stat. § 718.303 - the same hearing process applies to condominium associations.",
        ],
        homeowners: [
          "Check the date on the written notice you received.",
          "Confirm the hearing committee is not made up of officers, directors, or employees.",
        ],
        mistake: "Skipping the hearing - the fine cannot be imposed unless the committee approves it, so the hearing is your chance to show the violation was cured or did not happen.",
        nextStep: "Draft a hearing request",
        references: ["Fla. Stat. § 720.305", "Fla. Stat. § 718.303", "Your CC&Rs"],
      },
      sampleProductName: "Florida HOA Law AI",
      sampleLawHeading: "Relevant Florida Law",
      trustPoints: [
        "Answers include citations to the applicable law",
        "Reads your CC&Rs and bylaws alongside state law",
        "Drafts requests and letters when available",
      ],
      starterQuestions: [
        "Can the board fine me without a hearing?",
        "What records am I entitled to inspect?",
        "Does Chapter 718 or 720 apply to my community?",
      ],
      quickStartTitle: "What you can do with Lexi",
      quickStartSubtitle: "No legal background required.",
      quickStartSteps: [
        { title: "Ask about HOA law", description: "Ask your question in everyday language." },
        { title: "Add your governing documents", description: "Upload your CC&Rs, bylaws, rules, or notices when they matter to the answer." },
        { title: "Take the next step", description: "Draft a request, response, notice, or other HOA document when available." },
      ],
      audienceTitle: "Who uses FloridaHOALaw.ai",
      audiences: [
        { title: "Homeowners", description: "Understand an HOA rule, notice, responsibility, assessment, or proposed change under Florida law." },
        { title: "Board members", description: "Research meetings, elections, budgets, and enforcement, whether your HOA is self-managed or professionally managed." },
        { title: "Managers", description: "Get faster first-pass research before involving an attorney." },
      ],
      ctaTitle: "Ready to research Florida HOA law?",
      ctaDescription: "Ask your question and get an answer with citations to the applicable law.",
      ctaButton: "Ask a Florida HOA or Condo Question",
    },
    dashboard: {
      title: "Florida HOA Dashboard",
      assistantTitle: "Florida HOA Law AI Assistant",
      assistantDescription: "Get answers to your homeowner questions",
      assistantBody: "Chat about HOA law and your situation. If you uploaded documents below, open the AI Chat and turn on Answer from my documents to ask questions using those files.",
      quickChatDescription: "HOA help & questions about your uploaded documents",
    },
    chat: {
      emptyPrompt: "Ask about Florida HOA laws, Condominium Act, or your homeowner rights.",
      suggestedQuestions: [
        "What are the requirements for HOA board meetings in Florida?",
        "How do I file a complaint against my Florida HOA?",
        "What are the rules for HOA assessments in Florida?",
        "What disclosures must a Florida HOA provide?",
      ],
      compareWithLawTitle: "Compare with Florida HOA Law",
    },
  },
};
